import React from 'react';
import AuthStore from 'stores/AuthStore';
import UserActions from 'actions/UserActions';
import Team from 'records/Team';
import UserStore from 'stores/UserStore';
import connectToStores from 'alt-utils/lib/connectToStores';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import Breadcrumbs from 'components/pure/utils/Breadcrumbs';
import DirectReportsActions from 'actions/coaching/DirectReportsActions';
import TeamActions from 'actions/TeamActions';
import TeamListStore from 'stores/TeamListStore';
import SelectField from 'components/pure/utils/form/SelectField';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import buildUrl from './buildUrl';
import {List} from 'immutable';
import DashboardHistoryStore from 'stores/dashboard/DashboardHistoryStore';
import {getQuery} from 'utils/getQueryParam';
import {HISTORY_ACTIONS} from 'components/StatsPage2.utils';
import {HomeIcon} from '@components/icon/HomeIcon';
import './DashboardBreadcrumbs.styl';

// The ID of the quasi-team that represents all users reporting to a manager
const ALL_TEAM_ID = null;

const ALL_TEAM = new Team({id: ALL_TEAM_ID, name: '<All reports>'});
const MANAGER_ALL_TEAM = new Team({
  id: ALL_TEAM_ID,
  name: '<All of my reports>',
});

export function allTeam(dashboardManagerId, sessionUserId) {
  return dashboardManagerId === sessionUserId ? MANAGER_ALL_TEAM : ALL_TEAM;
}

@connectToStores
export default class DashboardBreadcrumbs extends React.PureComponent {
  static defaultProps = {
    // whether this widget will be responsible for loading it's own data
    // or will accept passed in data
    loadData: true,
  };

  static getStores() {
    return [DirectReportsStore, TeamListStore, UserStore];
  }

  static getPropsFromStores(props) {
    const {loadData = true} = props;
    if (!loadData) {
      return props;
    }
    const sessionUser = AuthStore.getState().user;
    const teams = props.managerId
      ? DirectReportsStore.getState().teams
      : sessionUser.isAdmin()
      ? TeamListStore.getState().teams.valueSeq()
      : List();
    const nonEmpties = teams.filter(
      team =>
        team.id === ALL_TEAM_ID ||
        !team.users || // some team responses don't have this `users` prop if so we just have to include it even if it may be empty
        team.users.size > 0
    );
    const {repId, coachId} = props;
    const repOrCoach = UserStore.getState().users.get(coachId ? coachId : repId);

    return {
      teams: nonEmpties,
      manager: UserStore.getState().users.get(props.managerId),
      rep: repOrCoach,
      users:
        props.selectedTeam || repOrCoach
          ? TeamListStore.getState().users.get(
              props.selectedTeam ? props.selectedTeam.id : repOrCoach.teamId
            )
          : null,
    };
  }

  componentDidMount() {
    if (!this.props.loadData) {
      return;
    }
    const sessionUser = AuthStore.getState().user;
    const {managerId, coachId, repId, teams} = this.props;
    const ids = List([managerId, coachId, repId].filter(id => id));
    if (ids.size > 0) {
      UserActions.fetchAll({ids}).then(users => {
        const repOrCoachId = coachId ? coachId : repId;
        const repOrCoach = repOrCoachId && users.find(user => user.id === repOrCoachId);
        repOrCoach && fetchTeamUsers(List.of(repOrCoach.teamId));
      });
    }
    if (managerId) {
      DirectReportsActions.directReports(managerId);
    } else if (sessionUser.isAdmin() || sessionUser.permissions.includes('can_view_dept_stats')) {
      TeamActions.fetchTeamsForUser();
    }
    if (teams) {
      fetchTeamUsers(teams.map(team => team.id));
    }
    this.setSelectedTeamByRep();
  }

  setSelectedTeamByRep() {
    // this rep may be a coach or a rep
    const {rep, teams, selectedTeam} = this.props;
    if (rep && teams) {
      const team = teams.find(team => team.id === rep.teamId);
      if (team && (!selectedTeam || team.id !== selectedTeam.id)) {
        this.props.onSelectTeam(team);
        const teamUsers = TeamListStore.getState().users.get(team.id);
        if (!teamUsers) {
          fetchTeamUsers(List.of(team.id));
        }
      }
    }
  }

  componentDidUpdate(prevProps) {
    // this rep may be a coach or a rep
    const {rep, teams, selectedTeam, coachId} = this.props;
    const {teams: prevTeams} = prevProps;
    if (!teams) {
      return;
    }
    if (!teams.equals(prevTeams)) {
      fetchTeamUsers(teams.map(team => team.id));
    }
    // if there is a rep, it determines the team (unless we're viewing a coach dash)
    if (rep && !coachId) {
      this.setSelectedTeamByRep();
    } else {
      const teamsPlusAll = this.getTeamsPlusAll(teams);
      if (!selectedTeam && teamsPlusAll && teamsPlusAll.size > 0) {
        this.props.onSelectTeam(teamsPlusAll.first());
      }
    }
  }

  getManagerBreadcrumbs() {
    const {coachId, managerId, adminId, manager, teams, repId} = this.props;
    const links = [];
    let coachingPlanLinkAdded = false;
    // being viewed by an admin
    if (adminId) {
      links.push({
        icon: <HomeIcon />,
        name: adminId === managerId ? 'Global Dashboard' : 'My Dashboard',
        url: buildUrl({adminId}),
      });

      coachingPlanLinkAdded = coachId ? false : this.maybeAddCoachingPlanLink(links);

      if (manager && adminId !== managerId) {
        const history = DashboardHistoryStore.getState().get('history');
        const lastPath = history && history.last();
        const state =
          repId && lastPath && lastPath.indexOf('/manager/') > 0
            ? {
                pathname: lastPath,
                action: HISTORY_ACTIONS.POP,
              }
            : {};
        links.push({
          name: manager.getFullName(),
          url: repId ? buildUrl({adminId, managerId}) : null,
          state,
        });
      }

      if (!coachingPlanLinkAdded && teams && teams.size > 1) {
        links.push(this.getTeamSelectorLink({showLabel: adminId === managerId}));
      } else if (adminId === managerId) {
        links.push({name: 'Team Dashboard'});
      }
    } else if (managerId) {
      coachingPlanLinkAdded = this.maybeAddCoachingPlanLink(links);
      if (!coachingPlanLinkAdded && teams && teams.size > 1) {
        links.push(this.getTeamSelectorLink({showLabel: true}));
      }
    }
    return {links, coachingPlanLinkAdded};
  }

  /**
   * Returns whether the link is added
   */
  maybeAddCoachingPlanLink(links) {
    const {coachId, managerId, adminId, isOnCoachingPlansSubPage} = this.props;
    if (isOnCoachingPlansSubPage) {
      links.push({
        name: 'Coaching Plans',
        url: buildUrl({adminId, managerId, coachId}) + '/coaching-plans',
      });
      return true;
    }
    return false;
  }

  maybeAddCoachingPlanLinkForRep(links) {
    const {location, isOnCoachingPlansWithPlan} = this.props;
    if (isOnCoachingPlansWithPlan) {
      links.push({
        name: 'Coaching Plans',
        url: location.pathname,
        query: {
          ...getQuery(location),
          planId: null,
        },
      });
    }
  }

  getExecBreadcrumbs() {
    let links = [];
    const {managerId, adminId} = this.props;
    if (adminId && !managerId) {
      links.push({
        icon: <HomeIcon />,
        name: 'Global Dashboard',
      });
    }
    this.maybeAddCoachingPlanLink(links);
    return links;
  }

  getRepBreadcrumbs(forCoach = false) {
    const {coachId, managerId, adminId, users, teams} = this.props;
    let links = [];
    if (managerId && !adminId) {
      links.push({
        icon: <HomeIcon />,
        name: 'My Dashboard',
        url: buildUrl({adminId, managerId}),
      });
    }
    let coachingPlanLinkAdded = false;
    if (adminId || managerId) {
      const managerBreadcrumbs = this.getManagerBreadcrumbs();
      links = links.concat(managerBreadcrumbs.links);
      coachingPlanLinkAdded = managerBreadcrumbs.coachingPlanLinkAdded;
    }
    if (!coachingPlanLinkAdded && managerId && users && users.size > 1) {
      links.push(
        this.getUserSelectorLink({
          showLabel: !adminId && teams && teams.size <= 1,
        })
      );
    }
    if (!coachId && !managerId && !adminId) {
      this.maybeAddCoachingPlanLinkForRep(links);
    }
    if (forCoach && !coachingPlanLinkAdded) {
      this.maybeAddCoachingPlanLink(links);
    }
    return links;
  }

  getCoachBreadcrumbs() {
    return this.getRepBreadcrumbs(true);
  }

  onSelectTeam = teamId => {
    if (teamId === ALL_TEAM_ID) {
      // the name of the ALL team does not matter here, just the ID
      this.props.onSelectTeam(ALL_TEAM);
    } else {
      const {teams} = this.props;
      const selected = teams && teams.find(team => team.id === teamId);
      selected && this.props.onSelectTeam(selected, true);
      fetchTeamUsers(List.of(teamId));
    }
  };

  /**
   * Given a list (non-empty) teams, sort them as needed,
   * then add an "ALL" team to the front if props.repId is not present.
   */
  getTeamsPlusAll(teams, doSort = false) {
    const sorted = doSort ? teams.sortBy(team => team.name.toLowerCase()) : teams;
    const sessionUser = AuthStore.getState().user;
    // if there is a repId we are in the hierarchy mode, and so the ALL team does not apply
    return !this.props.repId
      ? sorted.splice(0, 0, allTeam(this.props.managerId, sessionUser.id))
      : sorted;
  }

  getTeamSelectorLink({showLabel = false}) {
    const {selectedTeam, teams} = this.props;
    const teamOptions =
      teams &&
      this.getTeamsPlusAll(teams, true).map(team => ({
        label: team.name,
        value: team.id,
      }));
    return {
      name: (
        <FieldContainer className="selector-item" label={showLabel ? 'Team:' : null}>
          <SelectField
            className="team-selector"
            value={selectedTeam && selectedTeam.id}
            tether={true}
            options={teamOptions}
            hideResetButton={true}
            onChange={this.onSelectTeam}
          />
        </FieldContainer>
      ),
    };
  }

  getUserSelectorLink({showLabel = false}) {
    const {repId, coachId, users} = this.props;
    const repOrCoachId = coachId ? coachId : repId;
    const userOptions =
      users &&
      users.map(user => ({
        label: user.getFullName(),
        value: user.id,
      }));
    return {
      name: (
        <FieldContainer className="selector-item" label={showLabel ? 'User:' : null}>
          <SelectField
            value={repOrCoachId}
            className="team-selector"
            tether={true}
            options={userOptions}
            hideResetButton={true}
            onChange={this.props.onSelectRep}
          />
        </FieldContainer>
      ),
    };
  }

  render() {
    const breadcrumbLinks = this.props.repId
      ? this.getRepBreadcrumbs()
      : this.props.coachId
      ? this.getCoachBreadcrumbs()
      : this.props.managerId
      ? this.getManagerBreadcrumbs().links
      : this.getExecBreadcrumbs();
    return <Breadcrumbs className="dashboard-breadcrumbs" links={breadcrumbLinks} />;
  }
}

function fetchTeamUsers(teamIds) {
  const isAdmin = AuthStore.getState().user.isAdmin();
  if (isAdmin) {
    return TeamActions.fetchAllUsers.defer();
  }

  const missing = (List.isList(teamIds) ? teamIds : teamIds.toList()).filter(
    teamId => !areUsersLoadedOrLoading(teamId)
  );
  if (missing.size !== 0) {
    TeamActions.fetchUsers.defer(missing);
  }
}

function areUsersLoadedOrLoading(teamId) {
  const {users, usersLoading} = TeamListStore.getState();
  return users.get(teamId) || usersLoading.get(teamId);
}
